import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Message } from "../desktopClient/messages";
import type { RootState } from "../store";

/** Map of message name → negotiated version from the DC's SERVER_HELLO */
type NegotiatedMessages = Record<string, number>;

interface DesktopClientState {
  isConnected: boolean;
  desktopClientName?: string;
  connectionAttemptFailed: boolean;
  isAvailableLocally: boolean;
  /** Messages the DC accepts from the frontend (name → version) */
  supportedIncomingMessages: NegotiatedMessages;
  /** Messages the DC sends to the frontend (name → version) */
  supportedOutgoingMessages: NegotiatedMessages;
}

// Define the initial state using that type
const initialState: DesktopClientState = {
  isConnected: false,
  connectionAttemptFailed: false,
  isAvailableLocally: false,
  supportedIncomingMessages: {},
  supportedOutgoingMessages: {},
};

interface HandshakeCompletedPayload {
  name: string;
  supportedIncomingMessages: NegotiatedMessages;
  supportedOutgoingMessages: NegotiatedMessages;
}

const desktopClientSlice = createSlice({
  name: "desktopClient",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handshakeCompleted: (
      state,
      action: PayloadAction<HandshakeCompletedPayload>,
    ) => {
      state.isConnected = true;
      state.desktopClientName = action.payload.name;
      state.supportedIncomingMessages =
        action.payload.supportedIncomingMessages;
      state.supportedOutgoingMessages =
        action.payload.supportedOutgoingMessages;
      state.connectionAttemptFailed = false;
    },
    connectionLost: (state) => {
      state.isConnected = false;
      state.desktopClientName = undefined;
      state.connectionAttemptFailed = false;
      state.isAvailableLocally = false;
      state.supportedIncomingMessages = {};
      state.supportedOutgoingMessages = {};
    },
    connectionAttemptFailed: (state) => {
      state.connectionAttemptFailed = true;
    },
    availableLocally: (state) => {
      state.isAvailableLocally = true;
    },
  },
});

export const {
  handshakeCompleted,
  connectionLost,
  connectionAttemptFailed,
  availableLocally,
} = desktopClientSlice.actions;

export const selectDesktopClient = (state: RootState) => state.desktopClient;

export const selectDesktopClientName = createSelector(
  selectDesktopClient,
  (desktopClient) => desktopClient.desktopClientName,
);

/** Whether the DC supports receiving the request_authentication message */
export const selectIsDeviceAuthSupported = createSelector(
  selectDesktopClient,
  (desktopClient) =>
    Message.REQUEST_AUTHENTICATION in
    desktopClient.supportedIncomingMessages,
);

export default desktopClientSlice.reducer;
